import React, { useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

const validationSchema = yup.object({
  currentPassword: yup
    .string()
    .required('Current password is required'),
  newPassword: yup
    .string()
    .min(10, 'Password must be at least 10 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    )
    .required('New password is required'),
  newPasswordConfirm: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your new password'),
});

const ChangePassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const { showError, showSuccess } = useNotification();
  const { updateUser } = useAuth();
  const navigate = useNavigate();

  // Check if password change is required
  React.useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setIsRequired(user.must_change_password === true);
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await apiService.post('/auth/change-password', {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });

        // Update user object to clear must_change_password flag
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          user.must_change_password = false;
          localStorage.setItem('user', JSON.stringify(user));
          updateUser({ must_change_password: false });
        }

        showSuccess('Password changed successfully!');
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } catch (error: any) {
        const errorMessage = error.response?.data?.error ||
                           error.response?.data?.detail ||
                           'Failed to change password. Please try again.';
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 500,
            borderRadius: 2,
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              Change Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isRequired 
                ? 'You must change your temporary password before continuing.'
                : 'Update your password to keep your account secure.'
              }
            </Typography>
          </Box>

          {/* Security Notice */}
          {isRequired && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>Password Change Required:</strong> For your security, you must change your temporary password before accessing the system.
            </Alert>
          )}

          {/* Password Requirements */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Password Requirements:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
              <li>At least 10 characters long</li>
              <li>Contains uppercase and lowercase letters</li>
              <li>Contains at least one number</li>
              <li>Different from your current password</li>
            </Typography>
          </Alert>

          {/* Change Password Form */}
          <form onSubmit={formik.handleSubmit}>
            <FormControl
              fullWidth
              margin="normal"
              variant="outlined"
              error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
            >
              <InputLabel htmlFor="currentPassword">Current Password</InputLabel>
              <OutlinedInput
                id="currentPassword"
                name="currentPassword"
                type={showCurrentPwd ? 'text' : 'password'}
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="current-password"
                autoFocus
                label="Current Password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrentPwd(p => !p)} edge="end">
                      {showCurrentPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {formik.touched.currentPassword && formik.errors.currentPassword && (
                <FormHelperText>{formik.errors.currentPassword}</FormHelperText>
              )}
            </FormControl>

            <FormControl
              fullWidth
              margin="normal"
              variant="outlined"
              error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
            >
              <InputLabel htmlFor="newPassword">New Password</InputLabel>
              <OutlinedInput
                id="newPassword"
                name="newPassword"
                type={showNewPwd ? 'text' : 'password'}
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="new-password"
                label="New Password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPwd(p => !p)} edge="end">
                      {showNewPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {formik.touched.newPassword && formik.errors.newPassword && (
                <FormHelperText>{formik.errors.newPassword}</FormHelperText>
              )}
            </FormControl>

            <FormControl
              fullWidth
              margin="normal"
              variant="outlined"
              error={formik.touched.newPasswordConfirm && Boolean(formik.errors.newPasswordConfirm)}
            >
              <InputLabel htmlFor="newPasswordConfirm">Confirm New Password</InputLabel>
              <OutlinedInput
                id="newPasswordConfirm"
                name="newPasswordConfirm"
                type={showConfirmPwd ? 'text' : 'password'}
                value={formik.values.newPasswordConfirm}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="new-password"
                label="Confirm New Password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPwd(p => !p)} edge="end">
                      {showConfirmPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {formik.touched.newPasswordConfirm && formik.errors.newPasswordConfirm && (
                <FormHelperText>{formik.errors.newPasswordConfirm}</FormHelperText>
              )}
            </FormControl>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Change Password'
              )}
            </Button>

            {!isRequired && (
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/dashboard')}
                disabled={loading}
              >
                Cancel
              </Button>
            )}
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default ChangePassword;
